import { keepPreviousData, useInfiniteQuery, useMutation } from '@tanstack/react-query'

import useDialog from '@shared/hooks/src/useDialog'
import { setQueriesData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { filterCache, flatten, handleError, pageParam, QK } from '@shared/utils'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'

const LIMIT = 20

export function useUnmatchedLabs(options = {}) {
  const query = { limit: LIMIT }
  return useInfiniteQuery({
    queryKey: QK.labs.unmatched.list(query),
    queryFn: ({ pageParam }) => API.labs.unmatched.list({ ...query, offset: pageParam * LIMIT }),
    placeholderData: keepPreviousData,
    select: flatten,
    initialPageParam: 0,
    getNextPageParam: pageParam(LIMIT),
    ...options,
  })
}

export function useUnmatchedLabsCount() {
  const { data, hasNextPage } = useUnmatchedLabs({ enabled: false })

  const count = data ? `${data.filter((n) => !n.read).length}${hasNextPage ? '+' : ''}` : ''
  if (['0', '0+'].includes(count)) return ''
  return count
}

export function useUnmatchedLabDelete() {
  return useMutation({
    mutationFn: (labId) => API.labs.unmatched.id(labId).remove(),
    onSuccess: (data, unmatchedLabId) => {
      setQueriesData(
        { queryKey: QK.labs.unmatched.lists },
        filterCache((lab) => lab.id !== unmatchedLabId)
      )
    },
  })
}

export function useUnmatchedLabRelate() {
  return useMutation({
    mutationFn: ({ unmatchedLabId, relateLabId }) => API.labs.unmatched.id(unmatchedLabId).update({ match_lab_id: relateLabId }),
    onSuccess: (data, { unmatchedLabId }) => {
      setQueriesData(
        { queryKey: QK.labs.unmatched.lists },
        filterCache((lab) => lab.id !== unmatchedLabId)
      )
    },
  })
}

export function useUnmatchedLabMatchDialog({ onMatchSuccess }) {
  const relate = useUnmatchedLabRelate()

  return useDialog({
    component: ConfirmationV2,
    props: ({ item, close }) => {
      const { unmatchedLab, relateLab } = item

      return {
        title: 'Match Lab?',
        description: 'Are you sure you want to match this lab?',
        variant: 'success',
        onReject: () => close(),
        onConfirm: () =>
          relate
            .mutateAsync({ unmatchedLabId: unmatchedLab.id, relateLabId: relateLab.id })
            .then(() => onMatchSuccess?.())
            .catch((e) => handleError(e, { showResponse: true }))
            .finally(() => close()),
      }
    },
  })
}
